import { Injectable, inject } from '@angular/core';

import { ReplaySubject } from 'rxjs';
import { Dexie } from 'dexie';
import 'reflect-metadata/lite';

import { getAllMetadataKeys } from './models/base.model';
import { environment } from '../../../environments/environment';
import { ApiService } from '../services/api/api.service';

@Injectable({
  providedIn: 'root',
})
export class DbService {
  private apiService = inject(ApiService);

  private dbName = 'magis-erp-db';
  private db: Dexie = new Dexie(this.dbName);
  public dbIsReady = new ReplaySubject<boolean>(1);

  async initializeModels() {
    const exist = await Dexie.exists(this.dbName);
    if (!exist) {
      const rawModelsModule: object = await import('./models');
      const modelsModule: Record<string, object> = rawModelsModule as Record<
        string,
        object
      >;

      const stores: Record<string, string> = {};
      for (const entries of Object.entries(modelsModule)) {
        const model = entries[1];
        const modelTableName: string = (model as { tableName: string })
          .tableName;
        const metadataName = `${modelTableName}ModelFields`;
        const uniqueMetadataName = `${modelTableName}ModelUniqueFields`;
        const multiMetadataName = `${modelTableName}ModelMultiFields`;

        const getSchema = () => {
          const schema: string[] = [];

          const commonJsClassPropToExclude: string[] = [
            'constructor',
            'id',
            'prototype',
          ];

          const propNames =
            getAllMetadataKeys(
              metadataName,
              model as Record<string, string>
            )?.filter(
              (key: string | symbol) =>
                !commonJsClassPropToExclude.includes(key as string)
            ) || [];

          for (const prop of propNames) {
            schema.push(prop as string);
          }

          // UNIQUE PROPS

          const uniquePropNames =
            getAllMetadataKeys(
              uniqueMetadataName,
              model as Record<string, string>
            )?.filter(
              (key: string | symbol) =>
                !commonJsClassPropToExclude.includes(key as string)
            ) || [];
          for (const u_prop of uniquePropNames) {
            schema.push(`&${u_prop}` as string);
          }

          // MULTI PROPS

          const multiPropNames =
            getAllMetadataKeys(multiMetadataName, model)?.filter(
              (key: string | symbol) =>
                !commonJsClassPropToExclude.includes(key as string)
            ) || [];

          for (const m_prop of multiPropNames) {
            schema.push(`*${m_prop}` as string);
          }

          if (Array.isArray(schema) && schema.length) {
            return `++id, ${(schema as string[]).join(', ')}`;
          } else {
            return '++id';
          }
        };

        stores[modelTableName] = getSchema();
      }

      this.db.version(environment.appDbVersion).stores(stores);
    }
    this.db.on('ready', () => {
      this.dbIsReady.next(true);
    });

    this.db.open();
  }

  setLocalStorageUserToken(token: string) {
    this.apiService.setLocalToken(token);
  }

  getTable(tableName: string): Promise<unknown[]> | null {
    try {
      return this.db.table(tableName).toArray();
    } catch {
      return null;
    }
  }

  async getOnce<T>(tableName: string): Promise<T | null> {
    try {
      return (await this.db.table(tableName).orderBy(':id').first()) as T;
    } catch {
      return null;
    }
  }

  private add(tableName: string, data: object) {
    return this.db.table(tableName).add(data);
  }

  async addOnce(tableName: string, data: object) {
    const count = await this.db.table(tableName).count();

    if (!count) {
      return this.add(tableName, data);
    }
    return null;
  }

  async addOnceUpdate(tableName: string, data: object) {
    const row = await this.db.table(tableName).orderBy(':id').first();

    if (!row) {
      this.add(tableName, data);
    } else {
      this.update(tableName, row.id, data);
    }
  }

  private update(tableName: string, id: number, data: object) {
    return this.db.table(tableName).update(id, data);
  }

  clearTable(tableName: string) {
    this.db.table(tableName).clear();
  }

  close() {
    this.db.close();
  }

  async delete() {
    await this.db.delete();
  }
}
